/**
 * @file DefineFileTab.js
 * @description This file contains the DefineFileTab component, which is a form for defining the file details of an import template.
 */

import PropTypes from 'prop-types';
import ImportFileFormatDropdown from '../shared/asyncDropdowns/ImportFileFormatDropdown';
import ImportDelimiterDropdown from '../shared/asyncDropdowns/ImportDelimiterDropdown';
import style from './ImportTemplates.module.scss';
import { ImportFileFormat, ImportDelimiter } from '../../utils/data/enums';
import { getText } from '../../i18n';

const isColumnBasedFormat = formatId =>
    formatId === ImportFileFormat.Delimited ||
    formatId === ImportFileFormat.Excel;

const isFixedLengthFormat = formatId =>
    formatId === ImportFileFormat.FixedLength;

const getOrderedTemplateTypes = (templateTypes = []) =>
    [...templateTypes].sort(
        (a, b) => (a.processingOrder ?? 0) - (b.processingOrder ?? 0),
    );

const getCompatibleColumnPositionCount = (
    templateTypes = [],
    fallbackCount = 0,
) => {
    const firstTemplateType = getOrderedTemplateTypes(templateTypes)[0];
    const firstTemplateTypeFieldCount =
        firstTemplateType?.templateFields?.length || 0;

    return firstTemplateTypeFieldCount || fallbackCount || 0;
};

const clearTemplateTypeFieldsAndMappings = (
    templateTypes = [],
    templateId = 0,
) =>
    templateTypes.map((templateType, index) => ({
        ...templateType,
        importTemplateId: templateType?.importTemplateId ?? templateId,
        processingOrder: templateType?.processingOrder ?? index + 1,
        templateFields: [],
        importMapGroups: [],
    }));

const DefineFileTab = ({ templateData, setTemplateData }) => {
    const handleInputChange = event => {
        const { name, value } = event.target;
        setTemplateData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCheckboxChange = event => {
        const { name, checked } = event.target;
        setTemplateData(prevData => ({
            ...prevData,
            [name]: checked,
        }));
    };

    const handleColumnPositionCountChange = event => {
        const { value } = event.target;

        if (!isNaN(value))
            setTemplateData(prevData => ({
                ...prevData,
                columnPositionCount: Number(value),
            }));
    };

    const handleImportFileFormatChange = fileFormat => {
        const nextFileFormatId = fileFormat?.id ?? 0;
        const previousFileFormatId = templateData.importFileFormatId;

        let {
            sheetName,
            importDelimiterId,
            customDelimiter,
            hasTextQualifier,
            templateTypes = [],
        } = templateData;

        let nextColumnPositionCount = templateData.columnPositionCount || 0;

        if (nextFileFormatId === ImportFileFormat.Delimited) {
            sheetName = null;
        } else if (nextFileFormatId === ImportFileFormat.Excel) {
            importDelimiterId = null;
            customDelimiter = null;
            hasTextQualifier = false;
        } else if (nextFileFormatId === ImportFileFormat.FixedLength) {
            importDelimiterId = null;
            customDelimiter = null;
            sheetName = null;
            hasTextQualifier = false;
        }

        const switchingBetweenCompatibleColumnFormats =
            isColumnBasedFormat(previousFileFormatId) &&
            isColumnBasedFormat(nextFileFormatId);

        const switchingBetweenIncompatibleFormats =
            (isFixedLengthFormat(previousFileFormatId) &&
                isColumnBasedFormat(nextFileFormatId)) ||
            (isColumnBasedFormat(previousFileFormatId) &&
                isFixedLengthFormat(nextFileFormatId));

        if (switchingBetweenIncompatibleFormats) {
            templateTypes = clearTemplateTypeFieldsAndMappings(
                templateTypes,
                templateData.id,
            );
            nextColumnPositionCount = 0;
        } else if (switchingBetweenCompatibleColumnFormats) {
            nextColumnPositionCount = getCompatibleColumnPositionCount(
                templateTypes,
                templateData.columnPositionCount,
            );
        }

        setTemplateData(prevData => ({
            ...prevData,
            importFileFormatId: fileFormat?.id,
            importFileFormatName: fileFormat?.name,
            sheetName,
            importDelimiterId,
            customDelimiter,
            hasTextQualifier,
            templateTypes,
            columnPositionCount: nextColumnPositionCount,
        }));
    };

    const handleImportDelimiterChange = delimiter => {
        let customDelimiter = templateData.customDelimiter;

        if (delimiter?.id != ImportDelimiter.Custom) customDelimiter = null;

        setTemplateData(prevData => ({
            ...prevData,
            importDelimiterId: delimiter?.id,
            importDelimiterName: delimiter?.name,
            customDelimiter: customDelimiter,
        }));
    };

    return (
        <form className={`${style.defineFieldContainer} mt-5`}>
            <div className={`px-5 pb-1`}>
                <div className={`col-12 col-xs-12 col-md-12 col-lg-6 m-auto`}>
                    <div className="form-group mb-3  flex-sm-wrap flex-lg-nowrap ">
                        <label htmlFor="name">
                            {getText('msg_709a23220f2c')}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            id="name"
                            type="text"
                            className="form-control"
                            value={templateData.name || ''}
                            onChange={handleInputChange}
                            name="name"
                            required
                            autoFocus
                        />
                    </div>

                    <div className="form-group inline-input flex-sm-wrap flex-lg-nowrap mb-3">
                        <div className="form-group">
                            <input
                                id="hasHeaderRecord"
                                type="checkbox"
                                className="form-check-input"
                                checked={templateData.hasHeaderRecord || false}
                                onChange={handleCheckboxChange}
                                name="hasHeaderRecord"
                            />
                            <label
                                className="form-check-label mx-2"
                                htmlFor="hasHeaderRecord"
                            >
                                {getText('msg_a9602f66f421')}
                            </label>
                        </div>
                    </div>

                    <div className="form-group  flex-sm-wrap flex-lg-nowrap">
                        <ImportFileFormatDropdown
                            selectedFileFormat={{
                                id: templateData?.importFileFormatId,
                                name: templateData?.importFileFormatName,
                            }}
                            inlineLabel={false}
                            onFileFormatChange={handleImportFileFormatChange}
                            required
                        />
                    </div>
                    {templateData.importFileFormatId ===
                        ImportFileFormat.Excel && (
                        <div className="form-group mb-3  flex-sm-wrap flex-lg-nowrap">
                            <label htmlFor="sheetName">
                                {getText('msg_2766115baaaf')}
                            </label>
                            <input
                                id="sheetName"
                                type="text"
                                className="form-control"
                                value={templateData.sheetName || ''}
                                onChange={handleInputChange}
                                name="sheetName"
                            />
                        </div>
                    )}

                    {templateData.importFileFormatId ===
                        ImportFileFormat.Delimited && (
                        <>
                            <div className="form-group  flex-sm-wrap flex-lg-nowrap">
                                <ImportDelimiterDropdown
                                    selectedDelimiter={{
                                        id: templateData?.importDelimiterId,
                                        name: templateData?.importDelimiterName,
                                    }}
                                    inlineLabel={false}
                                    onDelimiterChange={
                                        handleImportDelimiterChange
                                    }
                                    required
                                />
                            </div>

                            {templateData.importDelimiterId ===
                                ImportDelimiter.Custom && (
                                <div className="form-group mb-3  flex-sm-wrap flex-lg-nowrap">
                                    <label htmlFor="customDelimiter">
                                        {getText('msg_9a71f27e6a94')}
                                    </label>
                                    <input
                                        id="customDelimiter"
                                        type="text"
                                        maxLength="1"
                                        className="form-control"
                                        value={
                                            templateData.customDelimiter || ''
                                        }
                                        onChange={handleInputChange}
                                        name="customDelimiter"
                                    />
                                </div>
                            )}

                            <div className="form-group mb-3 flex-sm-wrap flex-lg-nowrap inline-input ">
                                <div className="form-group">
                                    <input
                                        id="hasTextQualifier"
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={
                                            templateData.hasTextQualifier ||
                                            false
                                        }
                                        onChange={handleCheckboxChange}
                                        name="hasTextQualifier"
                                    />
                                    <label
                                        className="form-check-label mx-2"
                                        htmlFor="hasTextQualifier"
                                    >
                                        {getText('msg_1be27475a7d4')}
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-group mb-3  flex-sm-wrap flex-lg-nowrap">
                        {(templateData.importFileFormatId ===
                            ImportFileFormat.Delimited ||
                            templateData.importFileFormatId ===
                                ImportFileFormat.Excel) && (
                            <label htmlFor="columnPositionCount">
                                {getText('msg_44c6e77fb1ab')}
                                <span className="text-danger">*</span>
                            </label>
                        )}

                        {templateData.importFileFormatId ===
                            ImportFileFormat.FixedLength && (
                            <label htmlFor="columnPositionCount">
                                {getText('msg_e8b6b5206a33')}
                                <span className="text-danger">*</span>
                            </label>
                        )}

                        {(templateData.importFileFormatId ?? 0) !== 0 && (
                            <input
                                id="columnPositionCount"
                                type="number"
                                maxLength="3"
                                className="form-control"
                                value={templateData.columnPositionCount || ''}
                                onChange={handleColumnPositionCountChange}
                                name="columnPositionCount"
                            />
                        )}
                    </div>
                </div>
            </div>
        </form>
    );
};

DefineFileTab.propTypes = {
    templateData: PropTypes.object.isRequired,
    setTemplateData: PropTypes.func.isRequired,
};

export default DefineFileTab;
